import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { tasksAPI, usersAPI, leadsAPI, studentsAPI } from '../../services/api';
import { useRole } from '../../hooks/useRole';
import DependencyPicker from './DependencyPicker';

const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
const PATTERNS   = ['daily', 'weekly', 'monthly'];

export default function CreateTaskModal({ onClose, onCreated }) {
  const { user, isAdminOrAbove, isMD, isManager, isRCM, isIntern } = useRole();

  const [form, setForm] = useState({
    title: '', description: '', priority: 'Medium', category: '',
    tags: [], estimated_hours: '', due_date: '', assigned_to: '',
    link_type: '', lead_id: '', student_id: '',
    is_recurring: false, recurrence_pattern: 'daily', recurrence_end_date: '',
    depends_on: [],
  });
  const [tagInput, setTagInput]         = useState('');
  const [categories, setCategories]     = useState([]);
  const [users, setUsers]               = useState([]);
  const [submitting, setSubmitting]     = useState(false);

  // Lead search state
  const [leadQuery, setLeadQuery]       = useState('');
  const [leadResults, setLeadResults]   = useState([]);
  const [leadSearching, setLeadSearching] = useState(false);
  const [leadSelected, setLeadSelected] = useState(null);
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const leadSearchRef = useRef(null);

  // Student search state
  const [studentQuery, setStudentQuery]       = useState('');
  const [studentResults, setStudentResults]   = useState([]);
  const [studentSearching, setStudentSearching] = useState(false);
  const [studentSelected, setStudentSelected] = useState(null);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const studentSearchRef = useRef(null);

  // Load categories + users
  useEffect(() => {
    tasksAPI.getCategories().then(r => setCategories(r.data?.data?.categories || [])).catch(() => {});
    // Only load users if allowed to assign to others
    if (!isIntern) {
      usersAPI.getAll({ limit: 200 }).then(r => setUsers(r.data?.data?.users || [])).catch(() => {});
    }
  }, [isIntern]);

  // Search leads when query changes
  useEffect(() => {
    if (!leadQuery.trim() || form.link_type !== 'lead') { setLeadResults([]); return; }
    const timer = setTimeout(async () => {
      setLeadSearching(true);
      try {
        const res = await leadsAPI.getAll({ search: leadQuery.trim(), limit: 10 });
        setLeadResults(res.data?.data?.leads || []);
        setShowLeadDropdown(true);
      } catch { setLeadResults([]); } finally { setLeadSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [leadQuery, form.link_type]);

  // Search students when query changes
  useEffect(() => {
    if (!studentQuery.trim() || form.link_type !== 'student') { setStudentResults([]); return; }
    const timer = setTimeout(async () => {
      setStudentSearching(true);
      try {
        const res = await studentsAPI.getAll({ search: studentQuery.trim(), limit: 10 });
        setStudentResults(res.data?.data?.students || []);
        setShowStudentDropdown(true);
      } catch { setStudentResults([]); } finally { setStudentSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [studentQuery, form.link_type]);

  const selectLead = (lead) => {
    setLeadSelected(lead);
    setLeadQuery(lead.contact_name || lead.lead_id || '');
    set('lead_id', lead._id);
    setShowLeadDropdown(false);
  };

  const selectStudent = (student) => {
    setStudentSelected(student);
    setStudentQuery(student.student_name || student.name || student.student_id || '');
    set('student_id', student._id);
    setShowStudentDropdown(false);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addTag = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const tag = tagInput.trim();
      if (!form.tags.includes(tag)) set('tags', [...form.tags, tag]);
      setTagInput('');
    }
  };

  const removeTag = (tag) => set('tags', form.tags.filter(t => t !== tag));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        title:       form.title.trim(),
        description: form.description.trim() || undefined,
        priority:    form.priority,
        category:    form.category || undefined,
        tags:        form.tags.length ? form.tags : undefined,
        estimated_hours: form.estimated_hours ? Number(form.estimated_hours) : undefined,
        due_date:    form.due_date || undefined,
        assigned_to: form.assigned_to || undefined,
        lead_id:     form.link_type === 'lead'    ? form.lead_id    || undefined : undefined,
        student_id:  form.link_type === 'student' ? form.student_id || undefined : undefined,
        is_recurring:       form.is_recurring,
        recurrence_pattern: form.is_recurring ? form.recurrence_pattern : undefined,
        recurrence_end_date:form.is_recurring && form.recurrence_end_date ? form.recurrence_end_date : undefined,
        depends_on:  form.depends_on.length ? form.depends_on.map(d => d._id) : undefined,
      };
      const res = await tasksAPI.createTask(payload);
      const task = res.data?.data?.task;
      const assigneeName = task?.assigned_to?.name || 'assignee';
      toast.success(`Task ${task?.task_number || ''} created and assigned to ${assigneeName}`);
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 7,
    border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b',
    background: '#fff', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 640, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        {/* Header */}
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17, color: '#1B2A6B' }}>Create Task</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Fill in the details below to create a new task</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
          {/* Title */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Title <span style={{ color: '#ef4444' }}>*</span></label>
            <input style={inputStyle} placeholder="Task title…" value={form.title} onChange={e => set('title', e.target.value)} required />
          </div>

          {/* Description */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Description</label>
            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }} placeholder="Optional description…" value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          {/* Priority + Category */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Priority</label>
              <select style={inputStyle} value={form.priority} onChange={e => set('priority', e.target.value)}>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Category</label>
              <select style={inputStyle} value={form.category} onChange={e => set('category', e.target.value)}>
                <option value="">— None —</option>
                {categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          </div>

          {/* Due Date + Estimated Hours */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Due Date</label>
              <input type="date" style={inputStyle} value={form.due_date} onChange={e => set('due_date', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Estimated Hours</label>
              <input type="number" min="0" step="0.5" style={inputStyle} placeholder="e.g. 2.5" value={form.estimated_hours} onChange={e => set('estimated_hours', e.target.value)} />
            </div>
          </div>

          {/* Assigned To */}
          {!isIntern && (
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Assigned To</label>
              <select style={inputStyle} value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
                <option value="">— Self —</option>
                {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.sub_role || u.role})</option>)}
              </select>
            </div>
          )}

          {/* Tags */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Tags</label>
            <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '5px 8px', display: 'flex', flexWrap: 'wrap', gap: 5, cursor: 'text', background: '#fff' }}
              onClick={e => e.currentTarget.querySelector('input')?.focus()}>
              {form.tags.map(tag => (
                <span key={tag} style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: 4, padding: '2px 8px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#6366f1', fontSize: 14, lineHeight: 1 }}>×</button>
                </span>
              ))}
              <input
                style={{ border: 'none', outline: 'none', fontSize: 13, flex: 1, minWidth: 100, padding: '1px 2px', color: '#1e293b' }}
                placeholder={form.tags.length ? '' : 'Type tag and press Enter…'}
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={addTag}
              />
            </div>
          </div>

          {/* Linked To */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Linked To (optional)</label>
            <div style={{ display: 'flex', gap: 12, marginBottom: form.link_type ? 8 : 0 }}>
              {['', 'lead', 'student'].map(lt => (
                <label key={lt} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13, color: '#475569' }}>
                  <input type="radio" name="link_type" value={lt} checked={form.link_type === lt} onChange={() => {
                    set('link_type', lt);
                    set('lead_id', ''); set('student_id', '');
                    setLeadSelected(null); setLeadQuery(''); setLeadResults([]);
                    setStudentSelected(null); setStudentQuery(''); setStudentResults([]);
                  }} />
                  {lt === '' ? 'None' : lt === 'lead' ? '📋 Lead' : '👤 Student'}
                </label>
              ))}
            </div>

            {form.link_type === 'lead' && (
              <div style={{ position: 'relative' }} ref={leadSearchRef}>
                <input
                  style={inputStyle}
                  placeholder="Search lead by name, phone or ID…"
                  value={leadQuery}
                  onChange={e => { setLeadQuery(e.target.value); setLeadSelected(null); set('lead_id', ''); setShowLeadDropdown(true); }}
                  onFocus={() => { if (leadResults.length) setShowLeadDropdown(true); }}
                  autoComplete="off"
                />
                {leadSearching && (
                  <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#94a3b8' }}>Searching…</div>
                )}
                {showLeadDropdown && leadResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 7, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 200, maxHeight: 200, overflowY: 'auto' }}>
                    {leadResults.map(lead => (
                      <div key={lead._id}
                        onMouseDown={() => selectLead(lead)}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        <span style={{ fontWeight: 600, color: '#1B2A6B' }}>{lead.lead_id}</span>
                        {' — '}
                        <span style={{ color: '#334155' }}>{lead.contact_name || lead.type_specific_data?.name || lead.type_specific_data?.institution_name || '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
                {leadSelected && (
                  <div style={{ marginTop: 4, fontSize: 11, color: '#16a34a' }}>✓ Linked to {leadSelected.lead_id}</div>
                )}
              </div>
            )}

            {form.link_type === 'student' && (
              <div style={{ position: 'relative' }} ref={studentSearchRef}>
                <input
                  style={inputStyle}
                  placeholder="Search student by name, phone or ID…"
                  value={studentQuery}
                  onChange={e => { setStudentQuery(e.target.value); setStudentSelected(null); set('student_id', ''); setShowStudentDropdown(true); }}
                  onFocus={() => { if (studentResults.length) setShowStudentDropdown(true); }}
                  autoComplete="off"
                />
                {studentSearching && (
                  <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#94a3b8' }}>Searching…</div>
                )}
                {showStudentDropdown && studentResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 7, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 200, maxHeight: 200, overflowY: 'auto' }}>
                    {studentResults.map(student => (
                      <div key={student._id}
                        onMouseDown={() => selectStudent(student)}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        <span style={{ fontWeight: 600, color: '#1B2A6B' }}>{student.student_id || student._id}</span>
                        {' — '}
                        <span style={{ color: '#334155' }}>{student.student_name || student.name || '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
                {studentSelected && (
                  <div style={{ marginTop: 4, fontSize: 11, color: '#16a34a' }}>✓ Linked to {studentSelected.student_id || studentSelected._id}</div>
                )}
              </div>
            )}
          </div>

          {/* Recurring */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <div
                onClick={() => set('is_recurring', !form.is_recurring)}
                style={{ width: 36, height: 20, borderRadius: 10, background: form.is_recurring ? '#1B2A6B' : '#cbd5e1', position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s' }}>
                <div style={{ position: 'absolute', top: 3, left: form.is_recurring ? 18 : 3, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Recurring Task</span>
            </label>
            {form.is_recurring && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
                <div>
                  <label style={labelStyle}>Recurrence Pattern</label>
                  <select style={inputStyle} value={form.recurrence_pattern} onChange={e => set('recurrence_pattern', e.target.value)}>
                    {PATTERNS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Recurrence End Date</label>
                  <input type="date" style={inputStyle} value={form.recurrence_end_date} onChange={e => set('recurrence_end_date', e.target.value)} />
                </div>
              </div>
            )}
          </div>

          {/* Dependencies */}
          <div style={{ marginBottom: 6 }}>
            <label style={labelStyle}>Dependencies</label>
            <DependencyPicker
              value={form.depends_on}
              onChange={deps => set('depends_on', deps)}
            />
          </div>
        </form>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0 }}>
          <button type="button" onClick={onClose}
            style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="submit" form="" onClick={handleSubmit} disabled={submitting}
            style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: submitting ? '#94a3b8' : '#1B2A6B', color: '#fff', fontWeight: 700, fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer' }}>
            {submitting ? 'Creating…' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}
